import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'
import sanitizeHtml from 'sanitize-html'

import Typography from '@segment/matcha/components/Typography'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import AggregationImage from './AggregationImage'

const data = {
  heading: `Aggregation`,
  section1: `Individual events don’t tell the full story of a user. For that, you need to combine that data all into the notion of a single profile.`,
  section2: `This is where most systems hit their scaling limits. Not only do they need to process tens of thousands of events per second, but they need to route that data to a single partition. This requires:`,
  section3: `<ul><li>Querying a single profile in real-time in milliseconds</li>
  <li>Scanning across millions of user histories to find a small group</li></ul>`,
  section4: `Here’s how we do it.`,
  link: `https://www.youtube.com/watch?v=rECHcoGIJNU`,
  linkText: `Watch on Youtube (2,600 views)`,
}

const Aggregation: FC<PropsWithChildren<unknown>> = () => {
  const { heading, section1, section2, section3, section4, link, linkText } = data
  return (
    <Wrapper>
      <SectionBase variant="2Col" copyVariant="wide">
        <Section.Copy>
          <CopyTextBase>
            <CopyText.Heading>{heading}</CopyText.Heading>
            <CopyText.Description tag="div">
              <CopyTextDescriptionSection>{section1}</CopyTextDescriptionSection>
              <CopyTextDescriptionSection>{section2}</CopyTextDescriptionSection>
              <CopyTextDescriptionSection
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(section3, { allowedTags: [`ul`, `li`, `br`] }),
                }}
              />
              <CopyTextDescriptionSection>{section4}</CopyTextDescriptionSection>
              <CopyTextDescriptionSection>
                <Typography variant="pLarge">
                  <a href={link}>{linkText}</a>
                </Typography>
              </CopyTextDescriptionSection>
            </CopyText.Description>
          </CopyTextBase>
        </Section.Copy>
        <Section.Contents>
          <AggregationImage />
        </Section.Contents>
      </SectionBase>
    </Wrapper>
  )
}

const CopyTextDescriptionSection = styled.div`
  margin-bottom: 32px;
`

const Wrapper = styled.section`
  display: grid;
`

export default Aggregation
